import React, { useState, useEffect } from "react";
import { ReptoolModal } from "../../utilities/ReptoolModal.tsx";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faMinus, faPlus } from "@fortawesome/free-solid-svg-icons";
import { Button, FormControl } from "react-bootstrap";
import { makeMorphNameStringPotential } from "../../../utils/GeneCalculator.ts";
import { formatDate } from '../../utilities/FormatDate.ts';
import { BasicFetch } from "../../../utils/BasicFetch.ts";
import { Clutch } from "../../../types/Clutch.ts";
import { Gene } from "../../../types/Gene.ts";
import { PhenotypeCalculator } from "../../../utils/PhenotypeCalculator.ts";
import { FindMorphByGenes } from "../../utilities/FindMorph.ts";
import { isChanceGene } from "../../../types/Gene.ts";
import UpdateGeneticsModal from "./UpdateGeneticsModal.tsx";
import { driver } from "driver.js";
import { CollectionReptile } from "../../collection/types/CollectionTypes.ts";
import Lightbox from "yet-another-react-lightbox";
import { Morph } from "../../../types/Morph.ts";
import { useRepToolContext } from '../../../RepToolContext.tsx';


interface HatchedClutchModalProps {
  closeModal: () => void;
  editingClutch: Clutch;
  onSave: () => void;
  morphs: Morph[];
  genes: Gene[];
}
interface HatchedResult {
  displayName: string;
  genes: Gene[];
  picture: string | null;
  quantity: number;
}


const SelectedReptile = ({ reptile, morphs }: {
  reptile: CollectionReptile;
  morphs: Morph[];
}) => {
  const morph = FindMorphByGenes(reptile.genes, morphs);
  const picture = reptile?.picture || morph?.pictures?.[0]?.url || "images/notavailable.jpg";

  return (
    <div className="border rounded flex-fill">
      <div style={{
        height: '200px',
        width: '100%',
        overflow: 'hidden',
        backgroundImage: `url(${picture})`,
        backgroundSize: 'contain',
        backgroundRepeat: 'no-repeat',
        backgroundPosition: 'center',

      }}>

      </div>
      <div>{picture === morph?.pictures?.[0]?.url && <small>{morph?.pictures?.[0]?.credit}</small>}</div>
      <h6>{reptile.name || reptile.morph_name}</h6>
    </div>
  )
}

export const ResultTile = ({ result, updateRemainingEggs, remainingEggs, morphs, genes }: {
  result: HatchedResult;
  updateRemainingEggs?: (value: number) => void;
  remainingEggs?: number;
  morphs: Morph[];
  genes: Gene[];
}) => {
  if (result.genes.length === 0) {
    const wildType = genes.find((gene) => gene.gene_name === 'Wild Type');
    result.genes = [{ ...wildType, expression: 'NORMAL', gene_id: wildType.id }];
  }
  const [openLightbox, setOpenLightbox] = useState<boolean>(false);
  const fullName = makeMorphNameStringPotential(result.genes, morphs);
  const hasHoms = result.genes.filter((gene) => gene.expression === 'NORMAL' || gene.expression === 'SUPER').length > 0;
  let morph = FindMorphByGenes(result.genes, morphs);
  if (!hasHoms) {
    morph = morphs.find((morph) => morph.name === 'Wild Type')
  }

  const pictureSlides = morph?.pictures.map((picture) => {
    return {
      src: picture.url,
    }
  }) || [];


  const readOnly = remainingEggs === undefined || updateRemainingEggs === undefined;
  return (
    <div className="mb-2 col-6 col-md-4 col-lg-3 px-1 rep-result result-item" >
      <div className="border result-border text-center flex-fill d-flex flex-column h-100 overflow-hidden">
        <Lightbox
          open={openLightbox}
          close={() => setOpenLightbox(false)}
          slides={pictureSlides}
        />
        <div
          onClick={() => pictureSlides.length > 0 ? setOpenLightbox(true) : null}
          style={{
            height: '200px',
            overflow: 'hidden',
            backgroundImage: `url(${morph?.pictures?.[0]?.url || "images/notavailable.jpg"})`,
            backgroundSize: 'contain',
            backgroundRepeat: 'no-repeat',
            backgroundPosition: 'center',
          }}>

        </div>
        <div className='px-1'>
          <p style={{ lineHeight: 1 }}>{morph?.pictures?.[0]?.url && <small className='calculator-credit'>{morph?.pictures?.[0]?.credit}</small>}</p>
          {readOnly ? <h5>{fullName}</h5> : <h6 className="mt-3" style={{ fontSize: '.85rem' }}>{fullName}</h6>}
          {!readOnly && <>
            <hr />
            <div className="mx-1 my-3 mx-md-3 mt-auto">
              <h6 style={{ fontSize: '.85rem' }}>Quantity:</h6>
              <div className="d-flex">
                <Button variant="outline-light" size='sm' onClick={() => updateRemainingEggs(Math.max(result.quantity - 1, 0))}>
                  <FontAwesomeIcon icon={faMinus} />
                </Button>
                <FormControl
                  type="number"
                  min={0}
                  className="mx-2"
                  size='sm'
                  max={result.quantity + remainingEggs}
                  value={Math.max(result.quantity, 0)}
                  onChange={(e) => updateRemainingEggs(Math.min(parseInt(e.target.value) || 0, remainingEggs + result.quantity))}
                />

                <Button variant="outline-light" size='sm' onClick={() => updateRemainingEggs(remainingEggs > 0 ? result.quantity + 1 : result.quantity)}>
                  <FontAwesomeIcon icon={faPlus} />
                </Button>
              </div>
            </div>
          </>}
        </div>
      </div>
    </div>

  )
}



export const HatchedClutchModal = ({
  closeModal,
  editingClutch,
  onSave,
  morphs,
  genes
}: HatchedClutchModalProps) => {
  const results = PhenotypeCalculator(editingClutch.mother, editingClutch.father, morphs);
  const [hatchDate, setHatchDate] = useState<string>(new Date().toISOString());
  const [numberOfSlugs, setNumberOfSlugs] = useState<number>(editingClutch.number_of_slugs);
  const [saving, setSaving] = useState<boolean>(false);
  const [showProvenModal, setShowProvenModal] = useState<boolean>(false);
  const [genesProven, setGenesProven] = useState<Gene[]>([]);
  const { appendControls } = useRepToolContext();
  const [hatchedResults, setHatchedResults] = useState<HatchedResult[]>(results.map((result) => {
    return {
      genes: result,
      displayName: makeMorphNameStringPotential(result, morphs),
      picture: FindMorphByGenes(result, morphs)?.pictures?.[0]?.url || null,
      quantity: 0
    };
  }
  ) || []);
  const createHatched = () => {
    const creatingNumber = hatchedResults.reduce((accumulator, result) => {
      return accumulator + result.quantity;
    }, 0);
    if (creatingNumber === 0) {
      alert('No reptiles to add');
      setSaving(false);
      return;
    }
    if (creatingNumber > editingClutch.remaining_eggs) {
      alert('Too many reptiles to add');
      setSaving(false);
      return;
    }
    if (!hatchDate) {
      alert('Please select a hatch date');
      setSaving(false);
      return;
    }
    // check if any genes have been proven
    // first find all chance genes from the father and mother
    const fatherGenes = editingClutch.father.genes;
    const motherGenes = editingClutch.mother.genes;
    const allGenes = fatherGenes.concat(motherGenes);
    const chanceGenes = allGenes.filter((gene) => isChanceGene(gene));
    // find all recessive genes from the hatched results that are Normal expression
    const recessiveGenes = hatchedResults.filter(result => result.quantity > 0).map((result) => {
      return result.genes.filter((gene) => gene.expression === 'NORMAL' && gene.gene_type === 'Recessive');
    });
    // check if any of the recessive genes are in the chance genes
    const provenGenes = chanceGenes.filter((gene) => {
      return recessiveGenes.some((recessiveGene) => gene.name === recessiveGene.gene_name);
    });

    setGenesProven(provenGenes);

    BasicFetch({
      url: `${(window as any).serverLocation}clutch/${editingClutch.id}/create_hatched`,
      method: 'POST',
      contentType: 'application/json',
      data: {
        'hatch_date': hatchDate,
        'number_of_slugs': numberOfSlugs,
        'reptiles': hatchedResults.filter(result => result.quantity > 0).map(result => {
          const morphName = makeMorphNameStringPotential(result.genes, morphs);
          return {
            ...result,
            'morphName': morphName
          }
        })
      },
      onSuccess: (data) => {
        onSave();
        alert('Reptiles added to collection');
        if (provenGenes.length > 0) {
          setShowProvenModal(true);
        } else {
          closeModal();
        }
      },
      onFail: () => {
        alert('Error adding reptiles to collection');
      },
      onFinish: () => {
        setSaving(false);
      }
    })
  }
  useEffect(() => {
    if (saving) {
      createHatched();
    }
  }, [saving]);
  const remainingEggs = editingClutch.number_of_eggs - editingClutch.produced_reptiles.length - numberOfSlugs - hatchedResults.reduce((accumulator, result) => {
    return accumulator + result.quantity;
  }, 0)
  useEffect(() => {
    if (showProvenModal) {
      return;
    }
    appendControls(<div className="text-center w-100">
      <div className="mb-2">
        <span>
          Remaining Eggs: {remainingEggs}
        </span>
      </div>
      <span className="d-flex"  >
        <Button variant="outline-light" disabled={saving} onClick={() => setSaving(true)} className="mx-auto col-6">Add To My Collection</Button>
      </span>
    </div>, 'Hatched Results Modal');
  }, [editingClutch, remainingEggs, saving, numberOfSlugs]);

  const driverObj = driver({
    steps: [
      {
        element: '#create-clutch-modal #pairing',
        popover: {
          title: 'Pairing',
          description: 'The parents of the clutch.',
        },
      },
      {
        element: '#create-clutch-modal #hatch-date',
        popover: {
          title: 'Hatch Date',
          description: 'Select the date the eggs hatched.',
        }
      },
      {
        element: '#create-clutch-modal #number-of-slugs',
        popover: {
          title: 'Number of Slugs',
          description: 'Enter the number of slugs in the clutch.',
        }
      },
      {
        element: '#create-clutch-modal #results',
        popover: {
          title: 'Results',
          description: 'Enter the number of reptiles that hatched with each phenotype.',
        }
      }
    ]
  });
  const setUpCalendar = (value: Date) => {
    (window as any).setUpCalendar(
      "Hatch Date",
      () => (date: Date) => {
        setHatchDate(date);
        (window as any).closeCalendar();
      },
      value
    );
  }


  return (
    <>
      {showProvenModal &&
        <UpdateGeneticsModal
          closeModal={() => { setShowProvenModal(false); closeModal(); }}
          provenGenes={genesProven}
          editingClutch={editingClutch}
          onUpdate={() => {
            closeModal();
          }}
        />
      }
      <ReptoolModal
        id='create-clutch-modal'
        title="Hatched Results"
        closeModal={closeModal}
        showTutorial={() => driverObj.drive()}
        body={
          <div className="d-flex flex-column mb-5 pb-5">
            <div id='pairing' className="d-flex flex-row justify-content-around">
              <div className="text-center col-md-4 col-6">
                <h5>Sire:</h5>
                <div>
                  <SelectedReptile reptile={editingClutch?.father} morphs={morphs} />
                </div>
              </div>
              <div className="text-center col-6 col-md-4">
                <h5>Dam:</h5>
                <div>
                  <SelectedReptile reptile={editingClutch?.mother} morphs={morphs} />
                </div>
              </div>
            </div>
            <div id='hatch-date' className="mx-auto mt-3">
              <h6>Hatch Date</h6>
              <FormControl
                type="text"
                className="text-dark"
                value={formatDate(hatchDate).toLocaleDateString()}
                onClick={() => setUpCalendar(new Date(hatchDate))}
                readOnly
              />
            </div>
            <div id='number-of-slugs' className="mx-auto mt-3">
              <h6>Number of slugs</h6>
              <FormControl
                type="number"
                min={0}
                max={numberOfSlugs + remainingEggs}
                value={numberOfSlugs}
                onChange={(e) => setNumberOfSlugs(parseInt(e.target.value))}
              />
            </div>
            <hr />
            <div id='results' className="d-flex flex-wrap pb-5">
              {
                hatchedResults.map((result, x) => (
                  <ResultTile
                    key={x}
                    result={result}
                    morphs={morphs}
                    genes={genes}
                    remainingEggs={remainingEggs}
                    updateRemainingEggs={(quantity) => {
                      let newResults = [...hatchedResults];
                      newResults[x].quantity = quantity;
                      setHatchedResults(newResults);
                    }} />
                ))
              }
            </div>
          </div>
        } />
    </>

  )
}
